import { Close, Done } from "@mui/icons-material";
import {
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { ApiResponse } from "src/apis";
import Feature from "src/common_constants/Feature";
import { useSnackbar } from "src/context/ShowMessage";
import useLocalization from "src/hooks/use-localization";
import useDataDeletionRestoration from "src/hooks/useDataDeletionRestoration";
import { useAppSelector } from "src/redux/hooks";
import { selectHasFeature } from "src/redux/slices/featuresSlice";
import EmailDialog from "../email/EmailDialog";
import Iconify from "../Icons/Iconify";
import { FilterProps } from "../interfaces";
import NoRecordFound from "../NoRecordFound";
import TruncateText from "../TruncateText";
import CustomPagination from "./CustomPagination";
import DataTableActions from "./DataTableActions";
import FilterCustomization from "./FilterCustomization";
import ImageUploadDialog from "../fileUpload/ImageUploadDialog";
import FileUploadDialog from "../fileUpload/FileUploadDialog";
import { useEffect } from "react";
import { formatPhoneNumberForDisplay, isPhoneFieldName } from "src/utils";

export interface DataTableColumnProps extends FilterProps {
  id: string;
  label: string;
  minWidth?: number;
  maxWidth?: number;
  align?: "left" | "right" | "center" | "inherit";
  format?: (value: any) => string | React.ReactElement;
  render?: (value: any) => React.ReactElement;
  sortName?: string;
  sortable?: boolean;
  visible?: boolean;
  type?:
  | "boolean"
  | "date"
  | "datetime"
  | "currency"
  | "actions"
  | "description";
  onDeleteRow?: (id: number) => void;
  onEditRow?: (data: any) => void;
  onRestore?: (id: number) => void;
  editDisable?: (row: any) => boolean;
  deleteDisable?: (row: any) => boolean;
  isSticky?: boolean;
  isStickyPositionObject?: Object;

  showDelete?: boolean | ((row: any) => boolean);

  // Custom buttons support
  customButtons?: Array<{
    label: string;
    icon: string | React.ReactElement;
    onClick: (row: any) => void;
    color?: "primary" | "secondary" | "error" | "warning" | "info" | "success";
    show?: boolean | ((row: any) => boolean);
    disable?: boolean | ((row: any) => boolean);
    permission?: number;
  }>;
}

interface DataTableProps {
  rows: any[];
  //columns: DataTableColumnProps[];
  defaultColumns: DataTableColumnProps[];
  //setColumns?: React.SetStateAction<any>;
  filters?: any;
  setFilters?: React.SetStateAction<any>;
  onSearch?: (event?: React.FormEvent<HTMLFormElement>) => void;
  totalRowCount?: number;
  totalPageCount?: number;
  moduleName: string;
  disableColumnCustomization?: boolean;
  disableRowOnClick?: boolean;
  restoreData?: Function;
  fetchData?: Function;

  loading?: boolean;
  permissionDeleteRestore?: number;
  permissionUpdate?: number;
  resetData?: Object;
  isAssetPage?: boolean;
  idValue?: number | undefined | null;
  deleteData?: Function;
  onViewRow?: Function;
  isApiFilter?: boolean;
  isShowActionsNeeded?: boolean;
  allowCheckBoxSelect?: boolean; // if enabled selectedRows, setSelectedRows
  selectedRows?: any;
  setSelectedRows?: React.Dispatch<React.SetStateAction<any[]>>;
  handleCheck?: Function;
  fetchPDF?: Function;
  // sending email
  sendEmail?: Function;
  getEmailTemplate?: (id: number) => void;
  // sending file
  uploadFile?: (id: number, data: any) => Promise<ApiResponse>;
  uploadFileCallback?: Function;
  customDeleteMessage?: string;
  isHeaderSticky?: boolean;
}

const DataTable: React.FC<DataTableProps> = ({
  rows,
  defaultColumns,
  filters,
  setFilters,
  onSearch,
  totalRowCount,
  totalPageCount,
  moduleName,
  disableColumnCustomization,
  disableRowOnClick,
  restoreData,
  fetchData,
  loading,
  permissionDeleteRestore,
  permissionUpdate,
  isAssetPage,
  resetData,
  idValue,
  deleteData,
  onViewRow,
  isApiFilter = false,
  isShowActionsNeeded,
  allowCheckBoxSelect = false,
  selectedRows,
  setSelectedRows,
  handleCheck,
  sendEmail,
  getEmailTemplate,
  fetchPDF,
  uploadFile,
  uploadFileCallback,
  customDeleteMessage,
  isHeaderSticky = false,
}) => {
  const [columns, setColumns] = useState<DataTableColumnProps[]>(
    defaultColumns || []
  );
  const indexColumn = columns[0].id;
  const navigate = useNavigate();
  const location = useLocation();
  const { formatDate, formatDateTime, formatCurrency } = useLocalization();
  const { handleDeleteRestoreData } = useDataDeletionRestoration();
  const hasPhoneFormat = useAppSelector((state) =>
    selectHasFeature(state, Feature.PHONE_FORMAT),
  );
  type Order = "asc" | "desc";
  const { t } = useTranslation();

  const [order, setOrder] = useState<Order>("asc");
  const [orderBy, setOrderBy] = useState<string>(indexColumn);

  const hanleRowClick = (operation: string, id: number) => {
    const locPaths = location.pathname.split("/");

    const module = `${locPaths[1] === "Settings" ? locPaths[2] : locPaths[1]}`;

    if (locPaths[1] === "Settings") {
      navigate(`/Settings/${module}/${operation}-${module}`, { state: { id } });
    } else {
      navigate(`/${module}/${operation}-${module}`, { state: { id } });
    }
  };

  const hideTableCellSx = {
    display: "none",
  };

  const { displayMessage } = useSnackbar();

  const formatCellValue = (column: DataTableColumnProps, value: any) => {
    if (isPhoneFieldName(column.id)) {
      return formatPhoneNumberForDisplay(value, hasPhoneFormat);
    }

    return `${value ? value : "-"}`;
  };

  /** ------------------------------------------------------------------------ */

  //Will need it later
  // const emptyRows =
  //   filters.pageNumber > 0
  //     ? Math.max(0, (1 + filters.pageNumber) * filters.pageSize - rows?.length)
  //     : 0;

  /** ------------------------------------------------------------------------ */

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setFilters((f: any) => ({ ...f, pageNumber: newPage }));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFilters((f: any) => ({
      ...f,
      pageSize: parseInt(event.target.value),
      pageNumber: 1,
    }));
  };

  const handleSort = (property: string) => {
    if (filters?.sortBy && setFilters) {
      setFilters((f: any) => ({
        ...f,
        sortBy: property,
        sortDirection:
          f.sortBy === property && f.sortDirection === "asc" ? "desc" : "asc",
        pageNumber: 1,
      }));
    } else {
      const isAsc = orderBy === property && order === "asc";
      setOrder(isAsc ? "desc" : "asc");
      setOrderBy(property);
    }
  };

  function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
    if (b[orderBy] < a[orderBy]) {
      return -1;
    }
    if (b[orderBy] > a[orderBy]) {
      return 1;
    }
    return 0;
  }

  function getComparator<Key extends keyof any>(
    order: Order,
    orderBy: Key
  ): (
    a: { [key in Key]: number | string },
    b: { [key in Key]: number | string }
  ) => number {
    return order === "desc"
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  }

  function stableSort<T>(
    array: readonly T[],
    comparator: (a: T, b: T) => number
  ) {
    const stabilizedThis = array?.map(
      (el, index) => [el, index] as [T, number]
    );
    stabilizedThis?.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) {
        return order;
      }
      return a[1] - b[1];
    });
    return stabilizedThis?.map((el) => el[0]);
  }

  // Handle row selection
  const handleSelectRow = (newrow: any) => {
    const newSelectedRows = selectedRows.find(
      (item: any) => item.damageId === newrow.damageId
    )
      ? selectedRows.filter((row: any) => row.damageId !== newrow.damageId) // Deselect if already selected
      : [...selectedRows, newrow]; // Select the row

    setSelectedRows && setSelectedRows(newSelectedRows);
  };

  ///////////////////////////////////
  const determineSearchType = (
    searchText: string
  ): { searchType: number; searchNumber?: number } => {
    if (!searchText || searchText.trim() === "") {
      return { searchType: 0 }; // No filter
    } else if (!isNaN(Number(searchText))) {
      return { searchType: 1, searchNumber: Number(searchText) }; // Numeric filter
    } else {
      return { searchType: 2 }; // Text search
    }
  };
  //////////////////////////////////
  const sortedRows = React.useMemo(() => {
    if (isApiFilter) return rows;
    const { searchType, searchNumber } = determineSearchType(
      filters?.textSearch ?? ""
    );

    let copyOfRows = rows;
    totalRowCount = rows?.length || 0;
    switch (searchType) {
      case 1: // Numbers only
        if (searchNumber != null) {
          copyOfRows = copyOfRows.filter((item) =>
            Object.values(item).some((value) => value === searchNumber)
          );
        }
        break;
      case 2: // Text search
        if (filters.textSearch) {
          copyOfRows = copyOfRows.filter((item) =>
            (filters?.searchableFields || ["name"])?.some((field: string) =>
              item[field]
                ?.toString()
                .toLowerCase()
                .includes(filters?.textSearch?.toLowerCase())
            )
          );
        }
        break;
      default: // No filter
        break;
    }

    if (!filters) {
      return stableSort(copyOfRows, getComparator(order, orderBy));
    }

    const pageNumber = filters.pageNumber ?? 1;
    const pageSize = filters.pageSize ?? (rows?.length || 10);
    const startIndex = (pageNumber - 1) * pageSize;
    const endIndex = startIndex + pageSize;

    if (!filters.sortBy && !filters.sortDirection) {
      return stableSort(copyOfRows, getComparator(order, orderBy))?.slice(
        startIndex,
        endIndex
      );
    } else {
      return copyOfRows?.slice(startIndex, endIndex);
    }
  }, [order, orderBy, filters, setFilters, rows]);

  const [pdfLoading, setPdfLoading] = useState<boolean>(false);
  const [popupBlocked, setPopupBlocked] = useState({
    isBlocked: false,
    pdfUrl: "",
  });

  const fetchAndOpenPdf = async (id: any) => {
    setPdfLoading(true);
    try {
      const response: ApiResponse = fetchPDF && (await fetchPDF(id));
      if (response.status === 200) {
        const pdfUrl = URL.createObjectURL(response.data);
        const newTab = window.open(pdfUrl, "_blank");
        if (!newTab || newTab.closed || typeof newTab.closed === "undefined") {
          setPopupBlocked({ isBlocked: true, pdfUrl });
        } else {
          setPopupBlocked({ isBlocked: false, pdfUrl: "" });
        }
      } else {
        displayMessage("Failed to load pdf", "error");
      }
    } catch (error) {
      console.error("Error fetching PDF:", error);
    } finally {
      setPdfLoading(false);
    }
  };

  //email
  const [emailingId, setEmailingId] = useState<number>(0);
  const [emailDialogOpen, setEmailDialogOpen] = useState<boolean>(false);

  const onEmailClose = () => {
    setEmailDialogOpen(false);
  };

  const onEmailOpen = (id: number) => {
    setEmailingId(id);
    setEmailDialogOpen(true);
  };

  //file
  const [uploadId, setUploadId] = useState<number>(0);
  const [fileDialogOpen, setFileDialogOpen] = useState<boolean>(false);

  const onFileClose = () => {
    setFileDialogOpen(false);
  };

  const onFileOpen = (id: number) => {
    setUploadId(id);
    setFileDialogOpen(true);
  };

  useEffect(() => {
    setColumns(defaultColumns || []);
  }, [defaultColumns]);

  return (
    <Stack spacing={2} sx={{ width: "100%" }}>
      <Card sx={{ p: 2, width: "100%" }}>
        <TableContainer
          sx={{
            width: "100%",
            overflowX: "auto",
            ...(isHeaderSticky && { maxHeight: "calc(100vh - 260px)" }),
          }}
        >
          <Table stickyHeader={isHeaderSticky} sx={{ width: "100%" }}>
            <TableHead>
              <TableRow>
                {allowCheckBoxSelect && <TableCell sx={{ backgroundColor: "background.paper" }}></TableCell>}
                {columns.map((column, i) =>
                  column.id !== "actionButton" ? (
                    (column?.visible !== false ||
                      !column.hasOwnProperty("visible")) && (
                      <TableCell
                        key={i}
                        align={column.align}
                        style={{
                          minWidth: column.minWidth,
                          maxWidth: column.maxWidth,
                        }}
                        sx={
                          i === 0
                            ? { ...hideTableCellSx, backgroundColor: "background.paper" }
                            : {
                              backgroundColor: "background.paper",
                              ...(column.isSticky && {
                                position: "sticky",
                                zIndex: 3,
                                ...column?.isStickyPositionObject,
                              }),
                            }
                        }
                      >
                        {column.sortable ? (
                          <TableSortLabel
                            active={
                              (filters && filters?.sortBy
                                ? filters?.sortBy
                                : orderBy) ===
                              (column.sortName ? column.sortName : column.id) // need remove column.id
                            }
                            direction={
                              filters && filters?.sortBy
                                ? filters?.sortBy
                                  ? filters.sortDirection
                                  : "asc"
                                : order
                            }
                            onClick={() => {
                              handleSort(
                                column.sortName ? column.sortName : column.id
                              );
                            }} // need remove column.id
                          >
                            {column.label}
                          </TableSortLabel>
                        ) : (
                          column.label
                        )}
                      </TableCell>
                    )
                  ) : (
                    <TableCell
                      key={i}
                      align={column.align}
                      sx={{
                        minWidth: column.minWidth,
                        maxWidth: column.maxWidth,
                        backgroundColor: "background.paper",
                        ...(disableColumnCustomization ? { p: 2.5 } : {}),
                        ...(i === 0 ? hideTableCellSx : {}),
                        ...(column.isSticky && {
                          position: "sticky",
                          zIndex: 3,
                          ...column?.isStickyPositionObject,
                        }),
                      }}
                    >
                      {!disableColumnCustomization && (
                        <FilterCustomization
                          columns={columns}
                          setColumns={setColumns}
                          defaultColumns={defaultColumns}
                          icon={<Iconify icon={"heroicons-solid:menu-alt-3"} />}
                          title={t("common.tooltips.column")}
                          ignoreFirst={true}
                        />
                      )}
                    </TableCell>
                  )
                )}
              </TableRow>
            </TableHead>
            {loading && (
              <TableBody>
                {Array.from({ length: 5 }).map((_, rowIndex) => (
                  <TableRow key={rowIndex}>
                    {allowCheckBoxSelect && (
                      <TableCell padding="checkbox">
                        <Skeleton variant="rounded" width={24} height={24} />
                      </TableCell>
                    )}
                    {columns
                      .filter((column) => column.visible !== false)
                      .map((column, i) => (
                        <TableCell
                          key={`${column.id}-${rowIndex}`}
                          align={column.align}
                          sx={
                            i === 0
                              ? hideTableCellSx
                              : {
                                ...(column.isSticky && {
                                  position: "sticky",
                                  zIndex: 1,
                                  ...column?.isStickyPositionObject,
                                  backgroundColor: "background.paper",
                                }),
                              }
                          }
                        >
                          <Skeleton
                            variant="text"
                            width={column.type === "actions" ? 96 : "80%"}
                          />
                        </TableCell>
                      ))}
                  </TableRow>
                ))}
              </TableBody>
            )}
            {!loading && (
              <TableBody>
                {
                  rows &&
                  rows?.length > 0 &&
                  indexColumn &&
                  sortedRows?.map((row, index) => {
                    return (
                      <TableRow hover key={index}>
                        {allowCheckBoxSelect &&
                          handleSelectRow &&
                          handleCheck && (
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={handleCheck(row)}
                                onChange={() => handleSelectRow(row)}
                              />
                            </TableCell>
                          )}
                        {columns
                          .filter((column) => column.visible !== false)
                          .map((column, i) => {
                            const value = row[column.id];

                            return (
                              <TableCell
                                key={i}
                                align={
                                  column.type === "currency"
                                    ? "right"
                                    : column.align
                                }
                                sx={
                                  i === 0
                                    ? hideTableCellSx
                                    : {
                                      cursor:
                                        !disableRowOnClick &&
                                          column.id !== "actionButton"
                                          ? "pointer"
                                          : "default",
                                      ...(column.isSticky && {
                                        position: "sticky",
                                        zIndex: 1,
                                        ...column?.isStickyPositionObject,
                                        backgroundColor: "background.paper",
                                      }),
                                    }
                                }
                                onClick={
                                  !disableRowOnClick &&
                                    column.id !== "actionButton" &&
                                    !isAssetPage
                                    ? onViewRow
                                      ? () => onViewRow(row)
                                      : () =>
                                        hanleRowClick(
                                          "View",
                                          parseInt(
                                            row[indexColumn] as string,
                                            10
                                          )
                                        )
                                    : undefined
                                }
                              >
                                {column.format ? (
                                  column.format(value)
                                ) : column.type === "actions" ? (
                                  <DataTableActions
                                    customButtons={column?.customButtons}
                                    rowData={row}
                                    onEdit={() => {
                                      if (column?.onEditRow) {
                                        column?.onEditRow(row);
                                      } else {
                                        hanleRowClick(
                                          "Edit",
                                          parseInt(
                                            row[indexColumn] as string,
                                            10
                                          )
                                        );
                                      }
                                    }}
                                    // onView={
                                    //   !disableRowOnClick
                                    //     ? onViewRow
                                    //       ? () => onViewRow(row)
                                    //       : () =>
                                    //           hanleRowClick(
                                    //             "View",
                                    //             parseInt(
                                    //               row[indexColumn] as string,
                                    //               10
                                    //             )
                                    //           )
                                    //     : undefined
                                    // }

                                    onUpload={
                                      uploadFile
                                        ? () => {
                                          onFileOpen(row[indexColumn]);
                                        }
                                        : null
                                    }
                                    onSendEmail={
                                      sendEmail
                                        ? () => {
                                          onEmailOpen(row[indexColumn]);
                                        }
                                        : null
                                    }
                                    onPrint={
                                      fetchPDF
                                        ? () => {
                                          fetchAndOpenPdf(row[indexColumn]);
                                        }
                                        : null
                                    }
                                    onRestore={
                                      row["isActive"] === undefined
                                        ? undefined
                                        : () => {
                                          if (
                                            column?.onRestore &&
                                            isAssetPage
                                          ) {
                                            column?.onRestore(
                                              row[indexColumn]
                                            );
                                          } else {
                                            handleDeleteRestoreData(
                                              row[indexColumn],
                                              restoreData,
                                              fetchData,
                                              true,
                                              moduleName
                                            );
                                          }
                                        }
                                    }
                                    showDelete={
                                      column.showDelete === undefined
                                        ? true // Default to showing if not specified
                                        : typeof column.showDelete ===
                                          "function"
                                          ? column.showDelete(row)
                                          : column.showDelete
                                    }
                                    onDelete={
                                      column.showDelete === undefined ||
                                        (typeof column.showDelete === "function"
                                          ? column.showDelete(row)
                                          : column.showDelete)
                                        ? () => {
                                          column?.onDeleteRow
                                            ? column.onDeleteRow(
                                              row[indexColumn]
                                            )
                                            : handleDeleteRestoreData(
                                              row[indexColumn],
                                              deleteData,
                                              fetchData,
                                              false, // isRestore = false (delete)
                                              moduleName,
                                              undefined, // action (optional)
                                              undefined, // cancelCallback (optional)
                                              customDeleteMessage // 👈 Pass the custom message (if provided)
                                            );
                                        }
                                        : undefined
                                    }
                                    isActive={
                                      row["isActive"] !== undefined
                                        ? !!row["isActive"]
                                        : isShowActionsNeeded
                                    }
                                    permissionDeleteRestore={
                                      permissionDeleteRestore
                                    }
                                    permissionUpdate={permissionUpdate}
                                    disableButtons={
                                      row[indexColumn] === idValue
                                    }
                                    editDisable={
                                      column.editDisable &&
                                      column?.editDisable(row)
                                    }
                                    deleteDisable={
                                      row["isActive"] === undefined
                                        ? undefined
                                        : column.deleteDisable &&
                                        column?.deleteDisable(row)
                                    }
                                  />
                                ) : column.render ? ( //&& Array.isArray(value)
                                  column?.render(row)
                                ) : column.type === "boolean" ? (
                                  <>
                                    {value ? (
                                      <Done color="success" />
                                    ) : (
                                      <Close color="error" />
                                    )}
                                  </>
                                ) : // `${value}`
                                  column.type === "date" &&
                                    typeof value !== "number" ? (
                                    `${value ? formatDate(value) : "-"}`
                                  ) : column.type === "datetime" &&
                                    typeof value !== "number" ? (
                                    `${value ? formatDateTime(value) : "-"}`
                                  ) : column.type === "currency" &&
                                    typeof value === "number" ? (
                                    `${formatCurrency(value)}`
                                  ) : column.type === "description" ? (
                                    <TruncateText
                                      line={1}
                                      text={value ? value : "-"}
                                    />
                                  ) : (
                                    formatCellValue(column, value)
                                  )}
                              </TableCell>
                            );
                          })}
                      </TableRow>
                    );
                  })
                  // )
                }
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Card>
      {filters && rows && totalRowCount && totalRowCount > 0 ? (
        <CustomPagination
          totalRowCount={totalRowCount}
          filters={{
            pageSize: filters.pageSize,
            pageNumber: filters.pageNumber,
          }}
          setFilters={setFilters}
        />
      ) : null}
      {!loading && rows && rows?.length === 0 && (
        <Box
          width="100%"
          display="flex"
          justifyContent="center"
          alignItems="center"
          textAlign="center"
          marginTop={0}
          sx={{
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)", // Shadow properties
            border: "1px solid rgba(0, 0, 0, 0.1)", // Border properties
            borderRadius: "0 0 4px 4px", // Optional: Add border radius for rounded corners
            borderTop: "none", // Remove border from the top
            marginTop: "0px !important",
            padding: 1,
          }}
        >
          <NoRecordFound />
        </Box>
      )}

      {popupBlocked.isBlocked && (
        <Dialog
          open={popupBlocked.isBlocked}
          onClose={() => setPopupBlocked({ isBlocked: false, pdfUrl: "" })}
          aria-labelledby="popup-blocked-dialog-title"
          aria-describedby="popup-blocked-dialog-description"
          maxWidth="md"
          fullWidth
        >
          <DialogContent>
            <DialogContentText id="popup-blocked-dialog-description">
              The popup was blocked by your browser. Please enable popups to
              view the PDF.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button
              onClick={() => setPopupBlocked({ isBlocked: false, pdfUrl: "" })}
              color="secondary"
            >
              Cancel
            </Button>
            <Button
              onClick={() => {
                window.open(popupBlocked.pdfUrl, "_blank");
                setPopupBlocked({ isBlocked: false, pdfUrl: "" });
              }}
              color="primary"
            >
              Anyway Open Pdf
            </Button>
          </DialogActions>
        </Dialog>
      )}

      {emailingId > 0 && (
        <EmailDialog
          title={"Sent Email"}
          fetchData={() => getEmailTemplate && getEmailTemplate(emailingId)}
          open={emailDialogOpen}
          onClose={onEmailClose}
          sendFunction={sendEmail}
          id={emailingId}
        />
      )}
      {uploadFile && uploadId > 0 && (
        <FileUploadDialog
          title={"Add Files"}
          fieldId={uploadId}
          open={fileDialogOpen}
          onClose={onFileClose}
          addDocuments={uploadFile}
          callback={uploadFileCallback}
          permissionCreate={permissionUpdate}
          isImage={true}
          acceptedTypes={{
            "image/*": ["png", "gif", "jpg", "jpeg"],
          }}
        />
      )}
    </Stack>
  );
};

export default React.memo(DataTable);
